'use client';

import React, { useState } from 'react';

// Spam-protection: the address is passed in base64 (never as plaintext in the
// SSR HTML, so crawlers scraping the markup find no email / mailto). It's only
// decoded client-side after the user clicks "reveal". Then the leading eye icon
// turns into a copy icon that copies the address to the clipboard.

const iconCls = 'w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0';

const EyeIcon = () => (
  <svg className={iconCls} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
  </svg>
);

const CopyIcon = () => (
  <svg className={iconCls} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7z" />
    <path d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

export default function EmailReveal({ encoded }: { encoded: string }) {
  const [email, setEmail] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reveal = () => {
    try {
      setEmail(atob(encoded));
    } catch {
      setEmail('');
    }
  };

  const copy = async () => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable - ignore
    }
  };

  if (email === null) {
    return (
      <button
        type="button"
        onClick={reveal}
        className="inline-flex items-center gap-2 hover:font-bold transition-all cursor-pointer text-left align-middle"
      >
        <EyeIcon />
        <span>E-Mail Adresse anzeigen</span>
      </button>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={copy}
        className="flex-shrink-0 cursor-pointer"
        title={copied ? 'Kopiert!' : 'In die Zwischenablage kopieren'}
        aria-label={copied ? 'E-Mail-Adresse kopiert' : 'E-Mail-Adresse kopieren'}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
      <a href={`mailto:${email}`} className="hover:font-bold transition-all">
        {email}
      </a>
    </span>
  );
}
